import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type DocumentContentsAccessJSON = z.infer<typeof DocumentContentsAccessSchema.DocumentContentsAccessJSONZ>;

export type DocumentContentsAccessParams = z.infer<typeof DocumentContentsAccessSchema.DocumentContentsAccessZ>;

export default class DocumentContentsAccessSchema {
  public static readonly DocumentContentsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.DocumentContentsAccess),
    scope: z.enum(Scope).nullish(),
    subjectMatter: z.enum(DocumentSubjectMatter).nullish(),
  });

  public static readonly DocumentContentsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.DocumentContentsAccess),
    scope: z.enum(Scope).optional(),
    subjectMatter: z.enum(DocumentSubjectMatter).optional(),
  });

  public static validate(values: unknown): DocumentContentsAccessParams {
    return parseOrThrow(DocumentContentsAccessSchema.DocumentContentsAccessZ, values, {
      logError: true,
      schemaName: "DocumentContentsAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DocumentContentsAccessJSON {
    return parseOrThrow(DocumentContentsAccessSchema.DocumentContentsAccessJSONZ, values, {
      logError: true,
      schemaName: "DocumentContentsAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DocumentContentsAccessParams {
    try {
      DocumentContentsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DocumentContentsAccessJSON {
    try {
      DocumentContentsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DocumentContentsAccessJSON): DocumentContentsAccessParams {
    const parsed = parseOrThrow(DocumentContentsAccessSchema.DocumentContentsAccessJSONZ, data, {
      logError: true,
      schemaName: "DocumentContentsAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return DocumentContentsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: DocumentContentsAccessParams): DocumentContentsAccessJSON {
    const parsed = DocumentContentsAccessSchema.validate(notification);
    return DocumentContentsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    DocumentContentsAccessSchema.DocumentContentsAccessJSONZ,
    DocumentContentsAccessSchema.DocumentContentsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.DocumentContentsAccess as const,
        scope: data.scope ?? undefined,
        subjectMatter: data.subjectMatter ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.DocumentContentsAccess as const,
        scope: resource.scope,
        subjectMatter: resource.subjectMatter,
      }),
    }
  );
}
